import type { Locale } from '../i18n'

/**
 * Who QuickTide is open to, in words, from the account types the server allows to activate
 * just now (`Licence.openTo`, from REGISTER_TYPES). The welcome and the refusal both say it,
 * so a change on the server changes what the app says without a new release.
 *
 * The letters are Nautin's own account types: D donor, V volunteer, R relation, K/T/W the
 * members of Kustzeilers, Toerzeilers and Wadvaarders, P of PZV, M the KNRM. A (admin) and
 * N (unknown) are not anyone to address.
 */

interface Words {
  roles: Record<string, string>
  ofNautin: (list: string) => string
  clubs: Record<string, string>
  membersOf: (list: string) => string
  knrm: string
  and: string
}

const WORDS: Record<Locale, Words> = {
  nl: {
    roles: { D: 'donateurs', V: 'vrijwilligers', R: 'relaties' },
    ofNautin: list => `de ${list} van Nautin`,
    clubs: { K: 'Kustzeilers', T: 'Toerzeilers', W: 'Wadvaarders', P: 'PZV' },
    membersOf: list => `de leden van ${list}`,
    knrm: 'de KNRM',
    and: 'en'
  },
  en: {
    roles: { D: 'donors', V: 'volunteers', R: 'relations' },
    ofNautin: list => `the ${list} of Nautin`,
    clubs: { K: 'Kustzeilers', T: 'Toerzeilers', W: 'Wadvaarders', P: 'PZV' },
    membersOf: list => `the members of ${list}`,
    knrm: 'the KNRM',
    and: 'and'
  },
  de: {
    roles: { D: 'Spender', V: 'Freiwilligen', R: 'Partner' },
    ofNautin: list => `die ${list} von Nautin`,
    clubs: { K: 'Kustzeilers', T: 'Toerzeilers', W: 'Wadvaarders', P: 'PZV' },
    membersOf: list => `die Mitglieder von ${list}`,
    knrm: 'die KNRM',
    and: 'und'
  }
}

/** "a", "a en b", "a, b en c" */
function joined(items: string[], and: string): string {
  if (items.length <= 1) return items[0] ?? ''
  return `${items.slice(0, -1).join(', ')} ${and} ${items[items.length - 1]}`
}

/**
 * "de vrijwilligers van Nautin", "de donateurs en vrijwilligers van Nautin en de KNRM";
 * null when the list names nobody the reader could be (or there is no list at all).
 */
export function openToPhrase(types: string[] | null | undefined, locale: Locale): string | null {
  if (!types?.length) return null
  const w = WORDS[locale] ?? WORDS.nl
  const has = (t: string) => types.includes(t)
  const roles = Object.keys(w.roles).filter(has).map(t => w.roles[t]!)
  const clubs = Object.keys(w.clubs).filter(has).map(t => w.clubs[t]!)
  const parts = [
    ...(roles.length ? [w.ofNautin(joined(roles, w.and))] : []),
    ...(clubs.length ? [w.membersOf(joined(clubs, w.and))] : []),
    ...(has('M') ? [w.knrm] : [])
  ]
  return parts.length ? joined(parts, w.and) : null
}
